import { useTranslation } from "@bounce/i18n"
import type { PNImageProps } from "@bounce/web-components"

import type { GenericInterpolations } from "../../../../hooks/useGenericInterpolations"

import luggage from "@bounce/assets/big-icons/luggage.png"
import shield from "@bounce/assets/big-icons/shield-check.png"
import star from "@bounce/assets/big-icons/star.png"
import world from "@bounce/assets/big-icons/world.png"

type UseBenefitsOptions = {
  interpolations: GenericInterpolations
}

type Benefit = {
  title: string
  description: string
  src: PNImageProps["src"]
}

export const useBenefits = ({ interpolations }: UseBenefitsOptions): Benefit[] => {
  const { PRICE, NUM_CITIES, NUM_BAGS_STORED, INSURANCE_AMOUNT } = interpolations
  const { t } = useTranslation("marketing-ls-app")

  return [
    {
      src: world,
      title: t("hooks.useBenefits.benefit1.title", "Global luggage storage network in your pocket"),
      description: t(
        "hooks.useBenefits.benefit1.description",
        "With convenient locations in over {{numCities}} cities around the world, it’s easy to drop your stuff at a spot that works for you and your schedule.",
        {
          numCities: NUM_CITIES,
        },
      ),
    },
    {
      src: shield,
      title: t("hooks.useBenefits.benefit2.title", "Know your stuff is safe and sound"),
      description: t(
        "hooks.useBenefits.benefit2.description",
        "All bags are tagged, kept in dedicated luggage areas, and include protection up to {{bounceShieldProtection}} so you can have total peace of mind.",
        {
          bounceShieldProtection: INSURANCE_AMOUNT,
        },
      ),
    },
    {
      src: star,
      title: t("hooks.useBenefits.benefit3.title", "Do it all from the #1 luggage storage app", {
        numBagsStored: NUM_BAGS_STORED,
      }),
      description: t(
        "hooks.useBenefits.benefit3.descriptionV1",
        "Book on the go and head directly to the store via Uber, Google Maps, or Waze. For offline access and extra security, you can add your booking to your digital wallet.",
      ),
    },
    {
      src: luggage,
      title: t("hooks.useBenefits.benefit4.titleV1", "Leave your bags and other items behind"),
      description: t(
        "hooks.useBenefits.benefit4.descriptionV3",
        "Store your bags from {{luggagePricePerDay}}/day, and stay flexible with free cancellation anytime before your drop-off time.",
        {
          luggagePricePerDay: PRICE,
        },
      ),
    },
  ]
}
